import React from 'react'
import { FastField, useFormikContext } from 'formik'
import { Box, FormHelperText } from '@mui/material'
import { CustomTextField, CustomCheckboxWithoutInitialValue } from 'src/form'

import { ApolloQueryResult } from '@apollo/client'
import { HealthQuestionnaire } from 'src/participantHistory/shared/HealthQuestionnaire'
import { Section, SubSection } from '../common/Section'
import { IntakeNoteComprehensiveFormType } from './IntakeNoteComprehensiveFormSchema'
import { ReloadHealthQuestionnaire } from './ReloadHealthQuestionnaire'
import { GetIntakeNoteComprehensivePatientDataQuery } from './initialValues/getIntakeNoteComprehensivePatientData.generated'

export function ReasonForSeekTreatmentForm({
  refetch,
}: {
  refetch?: () => Promise<ApolloQueryResult<GetIntakeNoteComprehensivePatientDataQuery>>
}) {
  const { errors } = useFormikContext<IntakeNoteComprehensiveFormType>()

  return (
    <Section title="Reason for Seeking Treatment">
      <SubSection>
        {errors.REASON_FOR_SEEKING_TREATMENT?.HEALTH_QUESTIONNAIRE && (
          <Box mt={5} mb={2}>
            <ReloadHealthQuestionnaire refetch={refetch} />
          </Box>
        )}
        <HealthQuestionnaire fieldPath="REASON_FOR_SEEKING_TREATMENT.HEALTH_QUESTIONNAIRE" />
        {errors.REASON_FOR_SEEKING_TREATMENT?.HEALTH_QUESTIONNAIRE && (
          <FormHelperText error>Health questionnaire is required</FormHelperText>
        )}
      </SubSection>

      <SubSection title="Functional Impairments">
        <Box mt={2}>
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="REASON_FOR_SEEKING_TREATMENT.FUNCTIONAL_IMPAIRMENTS.DIFFICULTY_MEETING_EXPECTATIONS"
            label="Difficulty meeting expectations at work or school "
            helperText="Absenteeism, frequent mistakes, inability to focus on tasks."
          />
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="REASON_FOR_SEEKING_TREATMENT.FUNCTIONAL_IMPAIRMENTS.STRUGGLE_WITH_RELATIONSHIPS"
            label="Struggling with relationships"
            helperText="Withdrawing from friends or family, conflict with peers, difficulty trusting others."
          />
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="REASON_FOR_SEEKING_TREATMENT.FUNCTIONAL_IMPAIRMENTS.NEGLECT_OF_PERSONAL_NEEDS"
            label="Neglect of personal grooming, nutrition or medication routines"
            helperText="Poor bathing habits, skipping meals, missing prescribed doses of medication."
          />
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="REASON_FOR_SEEKING_TREATMENT.FUNCTIONAL_IMPAIRMENTS.DIFFICULTY_IN_SLEEP"
            label="Insomnia, hypersomnia or irregular sleep patterns"
            helperText="Difficulty falling or staying asleep, excessive daytime sleepiness."
          />
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="REASON_FOR_SEEKING_TREATMENT.FUNCTIONAL_IMPAIRMENTS.CHANGE_IN_APPETITE_OR_WEIGHT"
            label="Marked increase or decrease in appetite or body weight"
            helperText="Binge-eating episodes, loss of appetite, significant unintentional weight loss/gain."
          />
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="REASON_FOR_SEEKING_TREATMENT.FUNCTIONAL_IMPAIRMENTS.IMPAIRED_COGNITIVE_FUNCTIONING"
            label="Impaired cognitive functioning"
            helperText="Trouble staying on task, forgetfulness, difficulty processing information."
          />
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="REASON_FOR_SEEKING_TREATMENT.FUNCTIONAL_IMPAIRMENTS.VOLATILE_MOOD_RESPONSES"
            label="Volatile or blunted mood responses"
            helperText="Frequent irritability or anger outbursts, emotional numbness, difficulty calming down."
          />
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="REASON_FOR_SEEKING_TREATMENT.FUNCTIONAL_IMPAIRMENTS.DIFFICULTY_MANAGING_TASKS"
            label="Challenges managing household or other tasks"
            helperText="Forgetting to pay bills, inability to shop or prepare meals, neglecting chores."
          />
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="REASON_FOR_SEEKING_TREATMENT.FUNCTIONAL_IMPAIRMENTS.LOSS_OF_INTEREST"
            label="Loss of interest or pleasure in hobbies"
          />
          <FastField
            fullWidth
            component={CustomTextField}
            name="REASON_FOR_SEEKING_TREATMENT.FUNCTIONAL_IMPAIRMENTS.OTHER"
            label="Other functional impairments (if applicable)"
            multiline
            minRows={4}
          />
        </Box>
        {errors.REASON_FOR_SEEKING_TREATMENT?.FUNCTIONAL_IMPAIRMENTS && (
          <FormHelperText error>
            Please select at least one functional impairment from the list or write one that is not listed.
          </FormHelperText>
        )}
      </SubSection>

      <SubSection title="Narrative">
        <FastField
          fullWidth
          component={CustomTextField}
          name="REASON_FOR_SEEKING_TREATMENT.NARRATIVE"
          label="Narrative"
          helperText="Presenting problems and current symptoms."
          multiline
          minRows={4}
        />
      </SubSection>
    </Section>
  )
}
